/**
 * ModuleOverviewTab Component
 * 
 * Shows module overview with KPIs, status, and quick info.
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  AlertCircle, 
  AlertTriangle, 
  Brain, 
  BarChart3, 
  FileText, 
  CheckCircle,
  Clock,
  TrendingUp,
  X,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatWithUTC } from '@/lib/datetime';
import type { ModuleDefinition, ModuleState, DataGateStatus } from '@/lib/modules/types';

interface ModuleStats {
  totalFindings: number;
  criticalFindings: number;
  highFindings: number;
  mediumFindings: number;
  lowFindings: number;
  analysesRun: number;
  totalRows: number;
  lastAnalysisDate?: string;
}

interface ModuleOverviewTabProps {
  module: ModuleDefinition;
  moduleState: ModuleState;
  dataGateStatus: DataGateStatus;
  stats?: ModuleStats;
  signoff?: {
    signed_off_by: string;
    signed_off_at: string;
    comment?: string;
  };
  onSignOff: () => void;
  onRemoveSignOff: () => void;
  userDisplayName?: string;
}

const statusColors: Record<string, string> = {
  not_started: 'bg-muted text-muted-foreground',
  running: 'bg-primary/10 text-primary',
  completed: 'bg-success/10 text-success',
};

const statusLabels: Record<string, string> = {
  not_started: 'Ej startad',
  running: 'Pågår',
  completed: 'Klar',
};

export function ModuleOverviewTab({
  module,
  moduleState,
  dataGateStatus,
  stats,
  signoff,
  onSignOff,
  onRemoveSignOff,
  userDisplayName,
}: ModuleOverviewTabProps) {
  const { t } = useTranslation();
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);

  // Get translated description, fallback to module.description
  const translatedDescription = t(`modules.descriptions.${module.id}`, { defaultValue: module.description });

  return (
    <div className="space-y-6">
      {/* KPI Cards */}
      {stats && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900 border-blue-200 dark:border-blue-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-blue-600 dark:text-blue-400">{t('modules.overview.totalFindings', 'Totala fynd')}</p>
                  <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{stats.totalFindings}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-blue-600 dark:text-blue-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-red-100 dark:from-red-950 dark:to-red-900 border-red-200 dark:border-red-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-red-600 dark:text-red-400">{t('modules.overview.criticalHigh', 'Kritisk/Hög')}</p>
                  <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                    {stats.criticalFindings + stats.highFindings}
                  </p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-600 dark:text-red-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950 dark:to-green-900 border-green-200 dark:border-green-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-green-600 dark:text-green-400">{t('modules.overview.analysesRun', 'Analyser körda')}</p>
                  <p className="text-2xl font-bold text-green-900 dark:text-green-100">{stats.analysesRun}</p>
                </div>
                <Brain className="h-8 w-8 text-green-600 dark:text-green-400 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900 border-purple-200 dark:border-purple-800">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-purple-600 dark:text-purple-400">{t('modules.overview.rowsAnalyzed', 'Rader analyserade')}</p>
                  <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                    {stats.totalRows.toLocaleString()}
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-purple-600 dark:text-purple-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Findings Distribution */}
      {stats && stats.analysesRun > 0 && stats.totalFindings > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-5 w-5" />
              {t('modules.overview.findingsDistribution', 'Fördelning fynd')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-16 text-xs text-muted-foreground">{t('modules.overview.critical', 'Kritisk')}:</div>
                <Progress 
                  value={(stats.criticalFindings / stats.totalFindings) * 100} 
                  className="h-2 flex-1"
                />
                <div className="w-8 text-xs font-medium text-right">{stats.criticalFindings}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 text-xs text-muted-foreground">{t('modules.overview.high', 'Hög')}:</div>
                <Progress 
                  value={(stats.highFindings / stats.totalFindings) * 100} 
                  className="h-2 flex-1"
                />
                <div className="w-8 text-xs font-medium text-right">{stats.highFindings}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 text-xs text-muted-foreground">{t('modules.overview.medium', 'Medel')}:</div>
                <Progress 
                  value={(stats.mediumFindings / stats.totalFindings) * 100} 
                  className="h-2 flex-1"
                />
                <div className="w-8 text-xs font-medium text-right">{stats.mediumFindings}</div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-16 text-xs text-muted-foreground">{t('modules.overview.low', 'Låg')}:</div>
                <Progress 
                  value={(stats.lowFindings / stats.totalFindings) * 100} 
                  className="h-2 flex-1"
                />
                <div className="w-8 text-xs font-medium text-right">{stats.lowFindings}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Module Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5" />
            {t('modules.overview.aboutModule', 'Om modulen')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Expandable Description */}
          <Collapsible open={isDescriptionOpen} onOpenChange={setIsDescriptionOpen}>
            <div className="space-y-2">
              <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                {!isDescriptionOpen && translatedDescription}
              </p>
              <CollapsibleContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {translatedDescription}
                </p>
              </CollapsibleContent>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="h-auto p-0 text-primary hover:text-primary/80">
                  {isDescriptionOpen ? (
                    <span className="flex items-center gap-1">
                      {t('common.showLess', 'Visa mindre')}
                      <ChevronUp className="h-4 w-4" />
                    </span>
                  ) : (
                    <span className="flex items-center gap-1">
                      {t('common.readMore', 'Läs mer')}
                      <ChevronDown className="h-4 w-4" />
                    </span>
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </Collapsible>

          <div className="grid gap-3 pt-2 border-t">
            <div className="flex justify-between">
              <span className="text-sm">{t('modules.overview.status', 'Status')}:</span>
              <Badge className={statusColors[moduleState.status]} variant="outline">
                {statusLabels[moduleState.status]}
              </Badge>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">{t('modules.overview.requiredFiles', 'Obligatoriska filer')}:</span>
              <span className="text-sm font-medium">
                {moduleState.filesProgress.requiredUploaded}/{moduleState.filesProgress.required}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm">{t('modules.overview.optionalFiles', 'Valfria filer')}:</span>
              <span className="text-sm font-medium">
                {moduleState.filesProgress.optionalUploaded}/{moduleState.filesProgress.optional}
              </span>
            </div>
            {moduleState.lastRunAt && (
              <div className="flex justify-between">
                <span className="text-sm">{t('modules.overview.lastRun', 'Senaste körning')}:</span>
                <span className="text-sm font-medium">{formatWithUTC(moduleState.lastRunAt)}</span>
              </div>
            )}
          </div>

          {/* Signoff Section */}
          {moduleState.status === 'completed' && signoff && (
            <div className="space-y-2 pt-4 border-t">
              <div className="flex justify-between">
                <span className="text-sm">{t('modules.overview.signedOffBy', 'Avslutad av')}:</span>
                <span className="text-sm font-medium">{userDisplayName || signoff.signed_off_by}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm">{t('modules.overview.date', 'Datum')}:</span>
                <span className="text-sm font-medium">{formatWithUTC(signoff.signed_off_at)}</span>
              </div>
              {signoff.comment && (
                <div className="space-y-1">
                  <span className="text-sm">{t('modules.overview.comment', 'Kommentar')}:</span>
                  <p className="text-sm text-muted-foreground">{signoff.comment}</p>
                </div>
              )}
              <div className="pt-2">
                <Button 
                  onClick={onRemoveSignOff}
                  variant="outline"
                  className="w-full gap-2"
                >
                  <X className="h-4 w-4" />
                  {t('modules.overview.undoSignoff', 'Ångra avslut')}
                </Button>
              </div>
            </div>
          )}

          {moduleState.status !== 'completed' && moduleState.filesProgress.requiredUploaded > 0 && (
            <div className="pt-4 border-t">
              <Button 
                onClick={onSignOff}
                className="w-full gap-2"
              >
                <CheckCircle className="h-4 w-4" />
                {t('modules.overview.markComplete', 'Markera som klar')}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
